/**
 * Formats an ISO 8601 timestamp into a short "HH:mm" label used by the chat UI.
 * Falls back to an empty string for invalid input.
 */
export function formatChatTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
}

/** "ult. vez ayer a las 3:45 PM"-style label, or undefined when online. */
export function formatLastSeen(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const time = d.toLocaleTimeString('es', { hour: 'numeric', minute: '2-digit' });
  const day = d.toLocaleDateString('es', { day: 'numeric', month: 'short' });
  return `últ. vez ${day} ${time}`;
}

/**
 * Hora estilo WhatsApp para la lista de contactos del dashboard: hoy → hora
 * (HH:mm); ayer → "Ayer"; 2-6 días atrás → nombre del día de la semana
 * ("Miércoles"); más viejo → fecha corta, sin hora (con año si no es el año
 * actual, para no ser ambiguo entre "18 ago" de este año y de uno anterior).
 * Mismo patrón de buckets que `formatDashboardDateSeparator`
 * (`klab-chat.utils.ts`), pero esta función sí necesita el bucket de "hoy"
 * con hora, que aquella no tiene. Recibe epoch en ms (no ISO) porque
 * `contact.lastMessageAt` ya llega como `number` desde el mapper —
 * comparación de días por calendario local, no ventana rodante de 24h,
 * `now` inyectable para tests.
 */
export function formatContactListTime(
  epochMs: number | undefined,
  now = new Date(),
): string {
  if (epochMs === undefined || !Number.isFinite(epochMs)) return '';
  const d = new Date(epochMs);
  if (Number.isNaN(d.getTime())) return '';
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const diffDays = Math.round((nowStart - dayStart) / 86_400_000);
  if (diffDays === 0) {
    return d.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  }
  if (diffDays === 1) return 'Ayer';
  if (diffDays > 1 && diffDays <= 6) {
    const weekday = new Intl.DateTimeFormat('es-ES', { weekday: 'long' }).format(d);
    return weekday.charAt(0).toUpperCase() + weekday.slice(1);
  }
  const includeYear = d.getFullYear() !== now.getFullYear();
  return d.toLocaleDateString('es', {
    day: 'numeric',
    month: 'short',
    ...(includeYear ? { year: 'numeric' } : {}),
  });
}

/**
 * "18 ago 3:45 PM"-style day+time label for the "Vistos por" popover
 * (MOMSG-1081). "hoy"/"ayer" replace the day when applicable, comparing
 * local calendar days (not a 24h rolling window) against `now` — injectable
 * for tests, defaults to the real clock.
 */
export function formatSeenAt(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('es', { hour: 'numeric', minute: '2-digit' });
  const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const nowStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const diffDays = Math.round((nowStart - dayStart) / 86_400_000);
  if (diffDays === 0) return `hoy ${time}`;
  if (diffDays === 1) return `ayer ${time}`;
  const day = d.toLocaleDateString('es', { day: 'numeric', month: 'short' });
  return `${day} ${time}`;
}
